import { EbaySettingsResponse, ProductOption, SupplierIndexSupplier } from '../../../../types/api'
import { TemplateFormItem } from './types'

export const PRICE_OVERRIDES_DISABLED_PRICE_INPUT_TITLE_LABEL =
  'A price override is active, remove the override to edit'

export const findBestEbaySettingsMatch = (
  supplierId: number,
  suppliers: SupplierIndexSupplier[],
  ebayAccountData: EbaySettingsResponse[]
) => {
  const selectedSupplier = suppliers.find(s => s.id === supplierId)
  if (selectedSupplier && selectedSupplier.marketAccountIds.length) {
    // Find first account that matches the selected supplier
    return ebayAccountData.find(data => data.account.id === selectedSupplier.marketAccountIds[0])?.settings || null
  }

  // If supplier does not have an eBay account, select first acceptable account
  return ebayAccountData.find(data => data.account.matchesSupplierId)?.settings || null
}

export function getAutofilledSpecifics(specifics, templateForm: TemplateFormItem[]) {
  const autofilledSpecifics = {}

  templateForm?.forEach(templateFormValue => {
    const { label } = templateFormValue.props

    specifics.forEach(s => {
      if (s.name.toUpperCase() === label.toUpperCase()) {
        let specificValue

        if (templateFormValue.type === 'checkbox') {
          // templateFormValue.props.value should be a boolean if it came from a builder template checkbox
          specificValue = templateFormValue.props.value === true ? 'Yes' : 'No'
        } else {
          specificValue = templateFormValue.props.value // should be a string otherwise
        }

        if (specificValue) {
          // We may be setting specificValue to an invalid value here, if the specific's selectionMode != 'FreeText'.
          // It could be invalid if their builder template provided a value that ebay doesn't allow.
          // It could be invalid if it has the wrong case (uppercase/lowercase).
          // The SpecificRenderer component will be responsible for removing (or fixing) invalid values we set here.
          autofilledSpecifics[s.name] = specificValue
        }
      }
    })
  })

  return autofilledSpecifics
}

export function calculatePerformanceDuration(markName) {
  let duration = 0
  const entries = performance.getEntriesByType('mark').filter(m => m.name.match(markName))

  // Should always be a start and finish event
  if (entries.length === 0 || entries.length % 2 !== 0) return undefined

  entries.forEach(m => {
    if (m.name.includes(':end')) {
      duration += m.startTime
    }
    if (m.name.includes(':start')) {
      duration -= m.startTime
    }
  })

  return +(duration / 1000).toFixed(2)
}

export function generateVariantTitles(values: string[][]) {
  if (values.length <= 0) return []

  // This is just a cartesian product joined together by " / "
  // https://stackoverflow.com/questions/12303989/cartesian-product-of-multiple-arrays-in-javascript
  return values
    .reduce(
      (a, b) => {
        return a.map(x => b.map(y => x.concat([y]))).reduce((aa, bb) => aa.concat(bb), [])
      },
      [[]]
    )
    .map(v => v.join(' / '))
}

export function countVariations(options: ProductOption[]): number {
  return generateVariantTitles(options.filter(o => o.values.length > 0).map(o => o.values)).length
}

export function hasVariantErrorsOutsideOffset(errors: any, offSet: number) {
  return Object.keys(errors)
    .filter(v => v.includes('variants_'))
    .some(v => {
      const match = v.match(/\d+/)
      if (match) {
        const index = parseInt(match[0], 10)
        if (index >= offSet) return true
      }
      return false
    })
}
